import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '../components/ToastContext';
import useConnectionDetails from './useConnectionDetails';
import { ActiveView } from '../types';
import { AppConfig } from '../types/types';

interface UseVoiceSessionProps {
    activeView: ActiveView;
    onViewChange: (view: ActiveView) => void;
    hasSession: boolean;
    userName: string | null;
    sessionId: string | null;
    appConfig: AppConfig;
    onDisconnect?: () => void;
}

export const useVoiceSession = ({
    activeView,
    onViewChange,
    hasSession,
    userName,
    sessionId,
    appConfig,
    onDisconnect
}: UseVoiceSessionProps) => {
    const [isVoiceConnecting, setIsVoiceConnecting] = useState(false);
    const [isVoiceSessionActive, setIsVoiceSessionActive] = useState(false);
    const [isPreparingToken, setIsPreparingToken] = useState(false);
    const { showError, showWarning } = useToast();

    const hasPreFetchedRef = useRef(false);
    const isPreFetchingRef = useRef(false);
    const isDisconnectingRef = useRef(false);

    const {
        connectionDetails,
        existingOrRefreshConnectionDetails,
        refreshConnectionDetails
    } = useConnectionDetails(appConfig);

    // PRE-FETCH TOKEN: Only once when session is ready
    useEffect(() => {
        if (
            hasSession &&
            userName &&
            sessionId &&
            !connectionDetails &&
            !isPreparingToken &&
            !hasPreFetchedRef.current &&
            !isPreFetchingRef.current
        ) {
            hasPreFetchedRef.current = true;
            isPreFetchingRef.current = true;
            setIsPreparingToken(true);

            existingOrRefreshConnectionDetails()
                .then((details: { serverUrl: any; }) => {
                    if (details?.serverUrl) {
                        // showInfo('Voice Ready', 'Voice call feature is ready to use - socket connection verified');
                    } else {
                        // showInfo('Voice Ready', 'Voice call feature is ready to use');
                    }
                })
                .catch((error: { message: any; }) => {
                    hasPreFetchedRef.current = false;
                    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                    if (errorMsg.includes('Socket') || errorMsg.includes('socket')) {
                        showWarning(
                            'Socket Connection Warning',
                            'Voice server verification will be retried when you try to make a call.'
                        );
                    } else if (errorMsg.includes('fetch') || errorMsg.includes('network') || errorMsg.includes('Network')) {
                        // Don't show error on prefetch, just silently handle it
                        console.log('Voice prefetch skipped - network may be slow');
                    } else {
                        console.log('Voice prefetch warning:', errorMsg);
                    }
                })
                .finally(() => {
                    setIsPreparingToken(false);
                    isPreFetchingRef.current = false;
                });
        }
    }, [hasSession, userName, sessionId, connectionDetails, isPreparingToken, existingOrRefreshConnectionDetails, showWarning]);

    // Check if microphone device exists
    const checkIfMicExists = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasMic = devices.some(d => d.kind === "audioinput");

            if (!hasMic) {
                showError(
                    "No Microphone Detected",
                    "A microphone is not connected. Please plug in a mic or enable a built-in microphone."
                );
                return false;
            }

            return true;
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Device check failed';
            console.error('Mic device check error:', errorMsg);
            showError(
                "Device Check Failed",
                "A microphone is not connected. Please plug in a mic or enable a built-in microphone."
            );
            return false;
        }
    }, [showError]);

    // Check microphone access permission
    const checkMicrophoneAccess = useCallback(async () => {
        try {
            // First check if device even exists
            const micExists = await checkIfMicExists();
            if (!micExists) return false;

            // Check permission state using Permissions API
            try {
                const perm = await navigator.permissions.query({ name: "microphone" as PermissionName });

                if (perm.state === "denied") {
                    showError(
                        "Microphone Blocked",
                        "Microphone access is blocked in your browser. Please enable it in browser settings."
                    );
                    return false;
                }
            } catch (permErr) {
                // Permissions API might not be supported in all browsers, continue with getUserMedia
                console.log('Permissions API not supported, trying getUserMedia');
            }

            // Actual microphone access test
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Clean up the stream immediately after checking
            stream.getTracks().forEach(track => track.stop());

            return true;
        } catch (err: any) {
            console.error('Microphone access error:', err);

            // Handle specific getUserMedia errors
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                showError(
                    "Microphone Permission Denied",
                    "Please allow microphone access in your browser to start the voice call."
                );
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                showError(
                    "No Microphone Found",
                    "No microphone device was found. Please connect a microphone and try again."
                );
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                showError(
                    "Microphone In Use",
                    "Microphone is already in use by another application. Please close other apps and try again."
                );
            } else if (err.name === 'OverconstrainedError') {
                showError(
                    "Microphone Configuration Error",
                    "Unable to configure microphone with requested settings."
                );
            } else if (err.name === 'SecurityError') {
                showError(
                    "Security Error",
                    "Microphone access is blocked due to security restrictions."
                );
            } else {
                showError(
                    "Microphone Access Required",
                    "Microphone is not accessible. Please allow mic access to start the voice call."
                );
            }
            return false;
        }
    }, [showError, checkIfMicExists]);

    const handleStartVoiceSession = useCallback(async () => {
        if (isVoiceSessionActive || isVoiceConnecting) {
            return;
        }
        try {
            setIsVoiceConnecting(true);

            // Check microphone access BEFORE attempting connection
            const hasMicAccess = await checkMicrophoneAccess();
            if (!hasMicAccess) {
                onViewChange('chat');
                return;
            }

            // showInfo('Connecting to Voice', 'Generating access token...');

            const details = await existingOrRefreshConnectionDetails();
            if (!details) {
                throw new Error('No connection details available');
            }

            // showSuccess('Voice Ready', 'Connected to voice server - entering room now');

            setIsVoiceSessionActive(true);

        } catch (error) {
            console.error('❌ Voice session failed:', error);
            // Error messages are already shown by useConnectionDetails, no need to show duplicate messages here

            onViewChange('chat');
        } finally {
            setIsVoiceConnecting(false);
        }
    }, [isVoiceSessionActive, isVoiceConnecting, checkMicrophoneAccess, existingOrRefreshConnectionDetails, showError, onViewChange]);

    // Handle voice view connection
    useEffect(() => {
        if (activeView === 'voice' && !isVoiceConnecting && !isVoiceSessionActive) {
            setIsVoiceConnecting(true);
            handleStartVoiceSession();
        } else if (activeView !== 'voice' && isVoiceSessionActive) { }
    }, [activeView, isVoiceConnecting, isVoiceSessionActive, handleStartVoiceSession]);

    const handleDisconnect = useCallback(async () => {
        if (isDisconnectingRef.current) {
            return;
        }
        isDisconnectingRef.current = true;
        setIsVoiceSessionActive(false);

        if (onDisconnect) {
            onDisconnect();
        }

        onViewChange('chat');

        // showInfo('Call Ended', 'Voice call disconnected');
        hasPreFetchedRef.current = false;
        try {
            await refreshConnectionDetails();
            hasPreFetchedRef.current = true;
        } catch (error) {
            showWarning(
                'Token Refresh Failed',
                'Voice may not be available for next call. Please refresh if needed.'
            );
        } finally {
            setTimeout(() => { isDisconnectingRef.current = false; }, 500);
        }
    }, [onDisconnect, onViewChange, refreshConnectionDetails, showWarning]);

    const preflightVoiceCheck = useCallback(async () => {
        if (isPreparingToken || isVoiceConnecting) {
            return false;
        }
        setIsPreparingToken(true);
        try {
            // Check microphone access first
            const hasMicAccess = await checkMicrophoneAccess();
            if (!hasMicAccess) {
                return false;
            }

            const details = await existingOrRefreshConnectionDetails();
            if (!details) {
                // Error already shown by useConnectionDetails
                return false;
            }
            return true;
        } catch (err) {
            console.error('❌ Preflight voice check failed:', err);
            // Error messages are already shown by useConnectionDetails, no need to show duplicate messages here
            return false;
        } finally {
            setIsPreparingToken(false);
        }
    }, [checkMicrophoneAccess, existingOrRefreshConnectionDetails, isPreparingToken, isVoiceConnecting, showError]);

    return {
        isVoiceConnecting,
        isVoiceSessionActive,
        isPreparingToken,
        connectionDetails,
        handleStartVoiceSession,
        handleDisconnect,
        preflightVoiceCheck,
        checkMicrophoneAccess,
        checkIfMicExists
    };
};